import Button from "@ui/button/Button"
import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { systemErrorDetails, systemPost } from "src/services/superAdminApi"
import { AdminModal } from "./AdminModal"
import { EntityInput } from "./EntityInput"
import { Callout, Check, DangerZone, Fields, Muted, Row } from "./adminUi"

// Superadmin_User_Move_Invite_FE.md §1: a user always has exactly one company, so there is no "unlink", only a move.

export const ROLES = ["buyer", "teamlead", "admin"] as const
export type CompanyRole = typeof ROLES[number]
export interface MovePrefill { companyUid?: string; role?: CompanyRole; teamUid?: string }
interface MoveResult {
	to?: { company_name?: string; role?: string }
	from?: { company_name?: string }
	source_company_disabled?: boolean
}

/** Active teams of one company; system/transactions/filters already lists them per company. */
export const useCompanyTeams = (token: string, companyUid: string) => {
	const [teams, setTeams] = useState<Array<{ value: string; label: string }> | null>(null)
	useEffect(() => {
		let active = true
		setTeams(null)
		if (!companyUid) return
		// The company field changes on every keystroke while searching; wait until it settles.
		const timer = window.setTimeout(() => void systemPost<{ teams?: Array<{ value: string; label: string; status?: string }> }>(token, "system/transactions/filters", { company_uid: companyUid }).then((response) => {
			if (active) setTeams((response.data?.teams ?? []).filter((team) => !team.status || team.status === "active"))
		}), 300)
		return () => { active = false; window.clearTimeout(timer) }
	}, [token, companyUid])
	return teams
}

/** Role + team pickers shared by the move and the invite forms. A buyer needs a team, others may go without. */
export const RoleTeamFields = ({ teams, role, teamUid, onRole, onTeam, companyChosen }: {
	teams: Array<{ value: string; label: string }> | null; role: CompanyRole; teamUid: string; companyChosen: boolean
	onRole: (role: CompanyRole) => void; onTeam: (uid: string) => void
}) => <>
	<label>Role<select value={role} onChange={(event) => onRole(event.target.value as CompanyRole)}>
		{ROLES.map((item) => <option key={item} value={item}>{item === "teamlead" ? "Team lead" : item[0].toUpperCase() + item.slice(1)}</option>)}
	</select></label>
	<label>Team{role === "buyer" ? "" : " (optional)"}<select value={teamUid} disabled={!teams} onChange={(event) => onTeam(event.target.value)}>
		<option value=''>{!companyChosen ? "Choose a company first" : !teams ? "Loading teams…" : teams.length ? (role === "buyer" ? "Choose a team" : "No team") : "This company has no teams"}</option>
		{teams?.map((team) => <option key={team.value} value={team.value}>{team.label}</option>)}
	</select></label>
</>

export const UserCompanyMove = ({ token, userUid, userLabel, companyName, onChanged }: { token: string; userUid: string; userLabel: string; companyName: string; onChanged: () => Promise<void> | void }) => {
	const [open, setOpen] = useState(false)
	return <DangerZone title='Move to another company' hint='The user keeps login, password and KYC. Sessions close.'>
		<Row><Button variant='dangerSmall' onClick={() => setOpen(true)}>Move to company…</Button><Muted style={{ fontSize: 13 }}>Current: {companyName || "—"}</Muted></Row>
		{open && <MoveUserModal token={token} userUid={userUid} userLabel={userLabel} fromCompany={companyName} onClose={() => setOpen(false)} onMoved={onChanged} />}
	</DangerZone>
}

export const MoveUserModal = ({ token, userUid, userLabel, fromCompany, prefill, onClose, onMoved }: {
	token: string; userUid: string; userLabel: string; fromCompany: string; prefill?: MovePrefill; onClose: () => void; onMoved: () => Promise<void> | void
}) => {
	const [companyUid, setCompanyUid] = useState(prefill?.companyUid ?? "")
	const [role, setRole] = useState<CompanyRole>(prefill?.role ?? "buyer")
	const [teamUid, setTeamUid] = useState(prefill?.teamUid ?? "")
	const [disableSource, setDisableSource] = useState(false)
	const [understood, setUnderstood] = useState(false)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState("")
	const teams = useCompanyTeams(token, companyUid)
	const ready = !!companyUid && (role !== "buyer" || !!teamUid) && understood

	const move = async () => {
		setBusy(true); setError("")
		const response = await systemPost<MoveResult>(token, "system/user/company/move", {
			user_uid: userUid, company_uid: companyUid, role, ...(teamUid ? { team_uid: teamUid } : {}), disable_source_company: disableSource
		})
		setBusy(false)
		// The backend explains every refusal in `details`; it is shown as is.
		if (response.error) return setError(systemErrorDetails(response.error))
		const to = response.data?.to
		toast.success(`${userLabel} moved to ${to?.company_name ?? companyUid} as ${to?.role ?? role}. Sessions closed${response.data?.source_company_disabled ? `, ${response.data.from?.company_name ?? fromCompany} disabled` : ""}.`)
		onClose()
		await onMoved()
	}

	return <AdminModal title='Move to another company' subtitle={`${userLabel} · ${userUid}`} width={580} onClose={onClose}>
		<Callout tone='danger' title='The user leaves their current company.'>
			<ul>
				<li>All their sessions close; the next login opens the new company.</li>
				<li>They are removed from their current teams.</li>
				<li>The card issue limit is reset: the old company set it.</li>
				<li>Login, password and KYC move with them. Card holders stay; the first card in the new company reuses the identity at the issuer.</li>
			</ul>
		</Callout>
		<Fields style={{ gridTemplateColumns: "1fr" }}>
			<label>Move into company<EntityInput token={token} kind='company' name='companyUid' value={companyUid} onChange={(uid) => { setCompanyUid(uid); setTeamUid("") }} /></label>
			<RoleTeamFields teams={teams} role={role} teamUid={teamUid} companyChosen={!!companyUid} onRole={setRole} onTeam={setTeamUid} />
		</Fields>
		<div style={{ display: "grid", gap: 4 }}>
			<Check checked={disableSource} onChange={(event) => setDisableSource(event.target.checked)}>Disable company “{fromCompany || "current company"}”</Check>
			<Muted style={{ fontSize: 12.5, paddingLeft: 24 }}>Only if the user is its last member.</Muted>
		</div>
		{error && <Callout tone='danger'>{error}</Callout>}
		<Check checked={understood} onChange={(event) => setUnderstood(event.target.checked)}>I understand the consequences</Check>
		<Row style={{ justifyContent: "flex-end" }}>
			<Button variant='secondarySmall' onClick={onClose}>Cancel</Button>
			<Button variant='dangerSmall' disabled={!ready || busy} onClick={() => void move()}>{busy ? "Moving…" : "Move user"}</Button>
		</Row>
	</AdminModal>
}
